import { copy } from '@terminal/components/copy-to-clipboard/utils/copy';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useState } from 'react';

import { ConversationTask } from '../use-chat/use-chat.types';
import { useVisualizationStore } from '../use-visualization-store/useVisualizationStore';
import { CopyAction, copyResearchMessage } from '../utils/copyResearchMessage';
import { useConversationInventory } from './useConversationInventory';
import { useFormattedInventory } from './useFormattedInventory';

interface UseCopyConversationParams {
  task: ConversationTask;
  questionOnly: boolean;
  showingSteps: boolean;
  conversationId: string;
  action: CopyAction;
}

export const useCopyConversation = ({
  task,
  questionOnly,
  showingSteps,
  conversationId,
  action,
}: UseCopyConversationParams) => {
  const [isCopied, setIsCopied] = useState(false);
  const { trackAlfredChatCopy, trackAlfredConversationPrint } =
    usePosthogEvents();

  const { data: inventory } = useConversationInventory(conversationId, true);

  const formattedInventory = useFormattedInventory(inventory);

  const { charts, tables } = useVisualizationStore(state => ({
    charts: state.charts,
    tables: state.tables,
  }));

  const handleCopy = async () => {
    if (action === 'copy') {
      trackAlfredChatCopy(POSTHOG_TRACKING_SOURCES.ALFRED);
    } else if (action === 'print') {
      trackAlfredConversationPrint(POSTHOG_TRACKING_SOURCES.ALFRED);
    }

    if (questionOnly) {
      copy(task.input, task.input);
    } else {
      const states = task.states;

      if (states) {
        await copyResearchMessage({
          action,
          task: showingSteps
            ? task
            : { ...task, states: [states[states.length - 1]] },
          charts,
          tables,
          inventory: formattedInventory,
          taskQuestion: task.input,
        });
      }
    }

    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2_000);
  };

  return {
    isCopied,
    handleCopy,
  };
};
